import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { attendanceApi } from '@/lib/attendance-api'
import { trackEvent, Events } from '@/lib/observability'
import { useDeferredLoading } from '@/hooks/useDeferredLoading'
import type { AttendanceSummary, AttendanceChild } from '@/types/calendar'

function AttendanceCard({ child, onCheckIn, onCheckOut, isLoading }: {
  child: AttendanceChild
  onCheckIn: (childId: string) => void
  onCheckOut: (childId: string) => void
  isLoading: boolean
}) {
  const formatTime = (isoString?: string) => {
    if (!isoString) return ''
    return new Date(isoString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = () => {
    switch (child.status) {
      case 'checked_in':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Checked In
          </span>
        )
      case 'checked_out':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Checked Out
          </span>
        )
      case 'absent':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {child.absenceReason === 'holiday' ? 'Holiday' : 'Sick'}
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Expected
          </span>
        )
    }
  }

  return (
    <div className={`bg-white rounded-xl border p-4 ${child.status === 'absent' ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-primary-700 font-medium ph-no-capture">
              {child.firstName[0]}{child.lastName[0]}
            </span>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 ph-no-capture">
              {child.firstName} {child.lastName}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {getStatusBadge()}
              {child.checkedInAt && (
                <span>In: {formatTime(child.checkedInAt)}</span>
              )}
              {child.checkedOutAt && (
                <span>Out: {formatTime(child.checkedOutAt)}</span>
              )}
            </div>
          </div>
        </div>

        {child.status !== 'absent' && (
          <div className="flex gap-2">
            {child.status === 'expected' && (
              <button
                onClick={() => onCheckIn(child.id)}
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                Check In
              </button>
            )}
            {child.status === 'checked_in' && (
              <button
                onClick={() => onCheckOut(child.id)}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
              >
                Check Out
              </button>
            )}
            {child.status === 'checked_out' && (
              <button
                onClick={() => onCheckIn(child.id)}
                disabled={isLoading}
                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 disabled:opacity-50"
              >
                Check In Again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function AttendancePage() {
  const [attendance, setAttendance] = useState<AttendanceSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const showLoading = useDeferredLoading(isLoading)

  const loadAttendance = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await attendanceApi.getToday()
      setAttendance(data)
    } catch {
      setError('Failed to load attendance')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadAttendance()
  }, [])

  const handleCheckIn = async (childId: string) => {
    try {
      setActionLoading(true)
      await attendanceApi.checkIn(childId)
      trackEvent(Events.CHILD_CHECKED_IN)
      await loadAttendance()
    } catch {
      setError('Failed to check in')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCheckOut = async (childId: string) => {
    try {
      setActionLoading(true)
      await attendanceApi.checkOut(childId)
      trackEvent(Events.CHILD_CHECKED_OUT)
      await loadAttendance()
    } catch {
      setError('Failed to check out')
    } finally {
      setActionLoading(false)
    }
  }

  if (showLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Still loading but not showing skeleton yet - render nothing to prevent flash
  if (isLoading) {
    return null
  }

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  // Group children by status
  const expectedChildren = attendance?.expectedChildren.filter(c => c.status === 'expected') || []
  const checkedInChildren = attendance?.expectedChildren.filter(c => c.status === 'checked_in') || []
  const checkedOutChildren = attendance?.expectedChildren.filter(c => c.status === 'checked_out') || []
  const absentChildren = attendance?.expectedChildren.filter(c => c.status === 'absent') || []

  return (
    <div className="p-4 pb-20 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
        <p className="text-gray-500">{today}</p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Summary Stats */}
      {attendance && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-yellow-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-yellow-700">{attendance.summary.expected}</div>
            <div className="text-xs text-yellow-600">Expected</div>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{attendance.summary.checkedIn}</div>
            <div className="text-xs text-green-600">Checked In</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-gray-700">{attendance.summary.checkedOut}</div>
            <div className="text-xs text-gray-600">Left</div>
          </div>
          <div className="bg-red-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-red-700">{attendance.summary.absent}</div>
            <div className="text-xs text-red-600">Absent</div>
          </div>
        </div>
      )}

      {/* Expected Children */}
      {expectedChildren.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Waiting to Arrive</h2>
          <div className="space-y-3">
            {expectedChildren.map(child => (
              <AttendanceCard
                key={child.id}
                child={child}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                isLoading={actionLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Checked In Children */}
      {checkedInChildren.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Here Today</h2>
          <div className="space-y-3">
            {checkedInChildren.map(child => (
              <AttendanceCard
                key={child.id}
                child={child}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                isLoading={actionLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Checked Out Children */}
      {checkedOutChildren.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Gone Home</h2>
          <div className="space-y-3">
            {checkedOutChildren.map(child => (
              <AttendanceCard
                key={child.id}
                child={child}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                isLoading={actionLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Absent Children */}
      {absentChildren.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Absent Today</h2>
          <div className="space-y-3">
            {absentChildren.map(child => (
              <AttendanceCard
                key={child.id}
                child={child}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                isLoading={actionLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {attendance && attendance.expectedChildren.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No children</h3>
          <p className="text-gray-500 mb-4">Add children to start tracking attendance</p>
          <Link
            to="/children/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
          >
            Add Child
          </Link>
        </div>
      )}
    </div>
  )
}
